import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Button,
    Alert,
    Divider,
    Chip,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';
import EmailChipInput from '../../components/EmailChipInput';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

// Multi-select role: admin can tick any combination of roles or pick
// "Any role (everyone)" as the catch-all sentinel that the backend
// treats as "match every registered account". If "any" is combined
// with other picks the backend still resolves it as everyone.
const ROLE_OPTIONS = [
    { value: 'any', label: 'Any role (everyone)' },
    { value: 'parent', label: 'Parents' },
    { value: 'teacher', label: 'Teachers' },
    { value: 'student', label: 'Students' },
];

export default function EmailComposerPage() {
    const confirm = useConfirm();
    // Array so admin can tick multiple roles (e.g. Parents + Students).
    const [role, setRole] = useState([]);
    const [yearGroup, setYearGroup] = useState('');
    const [subject, setSubject] = useState('');
    const [q, setQ] = useState('');
    const [extraEmails, setExtraEmails] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [emailSubject, setEmailSubject] = useState('');
    const [body, setBody] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [attachments, setAttachments] = useState([]);
    const attachInputRef = useRef(null);

    useEffect(() => {
        axios.get(`${API_URL}/api/subjects`).then((res) => setSubjects(res.data || [])).catch(() => {});
        axios
            .get(`${API_URL}/api/admin/emails/templates`, authHeaders())
            .then((res) => setTemplates(res.data || []))
            .catch(() => {});
    }, []);

    // Any change to filters / extras invalidates the preview — clear it so the
    // displayed count can't disagree with what `Send` will actually do.
    useEffect(() => {
        setPreview(null);
    }, [role, yearGroup, subject, q, extraEmails]);

    const buildBody = () => ({
        filter: {
            // role: an array. Backend accepts a string (legacy), an array,
            // or the 'any' sentinel that matches every account. Empty
            // array = no role constraint.
            role: role.length ? role : undefined,
            yearGroup: yearGroup || undefined,
            subject: subject || undefined,
            q,
        },
        // Free-text emails the admin typed in are unioned with the filter
        // results on the server so a Year 9 send can include, say, an
        // external coordinator too.
        extraEmails,
        subject: emailSubject,
        htmlBody: body,
        attachments,
    });

    const handleAttach = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            setError('File is larger than 25 MB.');
            return;
        }
        try {
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const { data } = await axios.post(
                        `${API_URL}/api/admin/uploads/file`,
                        { base64: reader.result, filename: file.name, folder: 'emails' },
                        authHeaders()
                    );
                    setAttachments((a) => [
                        ...a,
                        { url: data.url, filename: data.filename || file.name, mime: file.type, size: data.size || file.size },
                    ]);
                } catch (err) {
                    setError(err?.response?.data?.message || 'Upload failed.');
                }
            };
            reader.readAsDataURL(file);
        } finally {
            if (attachInputRef.current) attachInputRef.current.value = '';
        }
    };

    const doPreview = async () => {
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/emails/preview`,
                buildBody(),
                authHeaders()
            );
            setPreview(data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const doSend = async () => {
        if (!emailSubject || !body) {
            setError('Subject and body are required.');
            return;
        }
        const total = (preview?.count ?? 0) + extraEmails.length;
        if (!(await confirm({ title: 'Send email', message: `Send to ${total || '?'} recipients?`, confirmLabel: 'Send' }))) return;
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/emails/send`,
                buildBody(),
                authHeaders()
            );
            setResult(data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const saveTemplate = async () => {
        if (!templateName.trim() || !emailSubject || !body) {
            setError('Template name, subject and body required.');
            return;
        }
        try {
            await axios.post(
                `${API_URL}/api/admin/emails/templates`,
                { name: templateName.trim(), subject: emailSubject, htmlBody: body },
                authHeaders()
            );
            const { data } = await axios.get(`${API_URL}/api/admin/emails/templates`, authHeaders());
            setTemplates(data || []);
            setTemplateName('');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save template.');
        }
    };

    const loadTemplate = (id) => {
        setSelectedTemplateId(id);
        if (!id) return;
        const t = templates.find((x) => x._id === id);
        if (t) {
            setEmailSubject(t.subject);
            setBody(t.htmlBody);
        }
    };

    return (
        <Container>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Compose email
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {result && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {result.sent === result.requested
                        ? `Email sent to ${result.sent} recipient(s).`
                        : `Sent to ${result.sent} of ${result.requested} recipient(s).`}
                    {result.failed ? ` ${result.failed} failed.` : ''}
                </Alert>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Recipients
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick everyone matching the filters below, then optionally add extra emails
                    that don't belong to a registered account.
                </Typography>
                    <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Pick recipients using the filters: choose one or more <strong>Roles</strong> (Parents, Teachers, Students) or select <strong>Any role (everyone)</strong>. Narrow further by year group, subject, or name/email.<br />
                            <strong>2.</strong> Add any extra addresses that aren't registered accounts in the <strong>Also send to</strong> field.<br />
                            <strong>3.</strong> Click <strong>Preview recipients</strong> to see the exact count and a sample of who will receive the email.<br />
                            <strong>4.</strong> Write the subject and body (rich text supported). Optionally attach a file.<br />
                            <strong>5.</strong> Optionally load a saved template or save this composition as a new template.<br />
                            <strong>6.</strong> Click <strong>Send</strong> — a confirmation dialog shows the total count before firing.<br />
                        </Typography>
                    </HowItWorksCard>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {/* Multi-select — pick any combination of roles or the
                        catch-all "Any role (everyone)". Selected roles render
                        as compact chips in the trigger. */}
                    <TextField
                        select
                        size="small"
                        label="Role"
                        value={role}
                        onChange={(e) => {
                            const next = e.target.value;
                            const arr = Array.isArray(next) ? next : [next];
                            // Picking "any" clears the other selections — it
                            // means "everyone" so keeping other role rows
                            // alongside it is meaningless.
                            if (arr.includes('any') && arr.length > 1) {
                                setRole(['any']);
                            } else {
                                setRole(arr);
                            }
                        }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {selected.map((v) => (
                                        <Chip
                                            key={v}
                                            size="small"
                                            label={ROLE_OPTIONS.find((o) => o.value === v)?.label || v}
                                        />
                                    ))}
                                </Stack>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    >
                        {ROLE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <YearGroupSelect
                        value={yearGroup}
                        onChange={setYearGroup}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField select size="small" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ minWidth: 200 }}>
                        <MenuItem value="">Any</MenuItem>
                        {subjects.map((s) => (
                            <MenuItem key={s._id} value={s.name}>{s.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Filter by name / email"
                        helperText="Narrows the matched recipients — doesn't add anyone new."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <Button onClick={doPreview} disabled={busy} variant="outlined" sx={{ alignSelf: 'flex-start', mt: '4px' }}>
                        Preview recipients
                    </Button>
                </Stack>

                <Box sx={{ mt: 3 }}>
                    <EmailChipInput
                        value={extraEmails}
                        onChange={setExtraEmails}
                        label="Also send to (extra emails)"
                        helperText="Press Enter to add. These addresses receive a copy in addition to the filter results above."
                    />
                </Box>

                {(preview || extraEmails.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Will send to{' '}
                            <strong>{(preview?.count ?? 0) + extraEmails.length}</strong> recipient(s)
                            {extraEmails.length > 0 && (
                                <>
                                    {' '}({preview?.count ?? 0} from filters + {extraEmails.length} extra)
                                </>
                            )}.
                        </Typography>
                        {preview?.sample?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Sample from filters: {preview.sample.join(', ')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Message
                </Typography>
                <Stack spacing={2}>
                    <TextField
                        select
                        size="small"
                        label="Load template"
                        value={selectedTemplateId}
                        onChange={(e) => loadTemplate(e.target.value)}
                        sx={{ minWidth: 240 }}
                    >
                        <MenuItem value="">— Select template —</MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        size="small"
                    />
                    <Box sx={{ '.ql-container': { minHeight: 240 } }}>
                        <ReactQuill theme="snow" value={body} onChange={setBody} />
                    </Box>

                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button onClick={() => attachInputRef.current?.click()} variant="outlined" size="small">
                                Attach file
                            </Button>
                            <input type="file" hidden ref={attachInputRef} onChange={handleAttach} />
                            <Typography variant="caption" color="text.secondary">
                                Up to 25 MB per file. PDFs, images, docs all OK.
                            </Typography>
                        </Stack>
                        {attachments.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {attachments.map((a, i) => (
                                    <Chip
                                        key={i}
                                        size="small"
                                        label={`📎 ${a.filename}`}
                                        onDelete={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Save as template
                </Typography>
                <Stack direction="row" spacing={2}>
                    <TextField
                        size="small"
                        label="Template name"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />
                    <Button variant="outlined" onClick={saveTemplate}>
                        Save template
                    </Button>
                </Stack>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" disabled={busy} onClick={doSend}>
                    {busy ? 'Sending…' : 'Send email'}
                </Button>
            </Box>
        </Container>
    );
}